import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/api';
import { useTheme } from '../hooks/useTheme';
import { useAuth } from '../hooks/useAuth';
import { CheckCircle, Crown, AlertCircle } from 'lucide-react';

const REDIRECT_DELAY_MS = 30000;

export default function MembershipSuccess() {
    const { isDarkMode } = useTheme();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [status, setStatus] = useState('verifying'); // 'verifying' | 'success' | 'error'
    const [membership, setMembership] = useState(null);
    const [errorMsg, setErrorMsg] = useState('');
    const { user, checkAuth } = useAuth();
    const authRefreshes = React.useRef(0);

    useEffect(() => {
        const sessionId = searchParams.get('session_id');
        if (!sessionId) {
            setStatus('error');
            setErrorMsg('No session ID found in URL.');
            return;
        }

        const verify = async () => {
            try {
                const response = await api.post('/membership/verify-session', {
                    session_id: sessionId,
                });
                setMembership(response.data.membership);
                setStatus('success');
                // Redirect to membership page after a delay long enough to read the confirmation
                setTimeout(() => navigate('/membership'), REDIRECT_DELAY_MS);
            } catch (err) {
                const msg = err?.response?.data?.error || err?.message || 'Could not verify your payment. Please contact support.';
                console.error('[MembershipSuccess] verify error:', msg, err?.response?.data);
                setErrorMsg(msg);
                setStatus('error');
            }
        };

        verify();
    }, [searchParams, navigate]);

    // A signup that just paid still has onboardingComplete=false cached, and the
    // app would send it back to the plan picker. Refresh the user once the payment
    // is confirmed (again if the page-load refresh comes back with the old value),
    // a few tries at most.
    useEffect(() => {
        if (status !== 'success' || user?.onboardingComplete !== false) return;
        if (authRefreshes.current >= 3) return;
        authRefreshes.current += 1;
        checkAuth();
    }, [status, user, checkAuth]);

    return (
        <div className={`flex items-center justify-center min-h-screen ${isDarkMode ? 'bg-surface' : 'bg-surface'}`}>
            <div className={`max-w-md w-full mx-4 rounded-3xl border shadow-lg p-10 text-center ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                {status === 'verifying' && (
                    <>
                        <div className="flex justify-center mb-6">
                            <div className="animate-spin rounded-full h-14 w-14 border-t-2 border-b-2 border-primary-500"></div>
                        </div>
                        <h2 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                            Confirming your payment...
                        </h2>
                        <p className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>
                            Please wait while we activate your membership.
                        </p>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <div className="flex justify-center mb-6">
                            <CheckCircle className="text-primary-500" size={64} />
                        </div>
                        <h2 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                            Payment Successful!
                        </h2>
                        {membership && (
                            <div className={`mt-4 mb-6 rounded-2xl p-4 ${isDarkMode ? 'bg-slate-700' : 'bg-primary-50'}`}>
                                <p className={`text-sm font-bold uppercase tracking-widest mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                    Your Plan
                                </p>
                                <p className={`text-xl font-bold flex items-center justify-center gap-2 ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                                    {membership?.TierName || membership?.tier_name || 'Active Plan'}
                                    <Crown className="text-amber-400" size={20} />
                                </p>
                            </div>
                        )}
                        <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            Redirecting you to your membership page...
                        </p>
                        <button
                            className="mt-4 px-6 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-xl font-semibold transition-all"
                            onClick={() => navigate('/membership')}
                        >
                            Go to My Membership
                        </button>
                    </>
                )}

                {status === 'error' && (
                    <>
                        <div className="flex justify-center mb-6">
                            <AlertCircle className="text-red-500" size={64} />
                        </div>
                        <h2 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                            Something went wrong
                        </h2>
                        <p className={`text-sm mb-6 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            {errorMsg}
                        </p>
                        <button
                            className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-semibold transition-all"
                            onClick={() => navigate('/membership/plans')}
                        >
                            Back to Plans
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
